import { useEffect, useState } from "react";
import { useApi } from "../../Helpers/ApiHook";
import { useSearchParams } from "react-router-dom";

const FilterGroup = ({
    showTitle = true,
    title,
    apiUrl = 'url',
    name = '',
    value = [],
    onChangeValue = null,
    isHideMsg = false,
    isNestedList = false
}) => {

    const [searchParams, setSearchParams] = useSearchParams();

    const { get } = useApi();
    const [isOpen, setIsOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [listData, setListData] = useState([]);

    const _getListData = async () => {
        setListData([]);
        setIsLoading(true);
        try {
            const data = await get(apiUrl, {});
            if (data?.status) {
                setListData(data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching listData: ', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (apiUrl) {
            _getListData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    // keep URL search params in sync with value
    useEffect(() => {
        if (!name) return;

        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            if (value && value.length > 0) {
                const paramValue = value.join(',');
                const currentValue = newParams.get(name);

                // Only update if the value has actually changed
                if (currentValue !== paramValue) {
                    newParams.set(name, paramValue);
                }
            } else {
                // Remove the parameter if value is empty
                if (newParams.has(name)) {
                    newParams.delete(name);
                }
            }

            return newParams;
        }, { replace: true });
    }, [value, name, setSearchParams]);

    // Determine content height limit based on nesting
    const maxHeightClass = isNestedList ? 'max-h-full' : 'max-h-48';

    // Determine scroll/spacing classes based on nesting
    const scrollClasses = isNestedList
        ? 'space-y-2' // Only spacing is needed when nested
        : 'overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400/50 scrollbar-track-gray-700/30 hover:scrollbar-thumb-gray-400/70 space-y-2';

    return (
        // Remove bottom margin (mb-4) and add minimal vertical padding (py-2) when nested
        <div className={`w-full flex flex-col items-center ${isNestedList ? 'py-1' : 'mb-4'}`}>
            {showTitle && (
                <div
                    className="w-full flex flex-row justify-between items-center cursor-pointer mt-5 mb-3"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="text-white text-md font-medium mb-1">{title}</div>
                    <div className={`transform transition-transform duration-300 text-2xl leading-none ${isOpen ? 'rotate-0' : 'rotate-180'}`}>
                        <img className="w-5 h-5 transition-transform duration-300" src="/assets/services/1/angle-down.png" alt="angle-down" />
                    </div>
                </div>
            )}

            {/* Content Container - Fixed Height for Scrolling */}
            <div
                className={`w-full flex flex-col justify-center items-start overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? `${maxHeightClass} opacity-100` : 'max-h-0 opacity-0'}`}
            >
                {isOpen && (
                    // This inner div handles the actual scrolling and appearance
                    <div
                        // If not nested, this div scrolls and spaces items.
                        // If nested, this div only spaces items, allowing parent to scroll.
                        className={`w-full h-full ${scrollClasses}`}
                        style={isNestedList ? {} : { scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) rgba(55, 65, 81, 0.3)' }}
                    >
                        {isLoading && (
                            <div className="flex justify-center py-2">
                                <span className="text-[0.75rem] text-gray-400 ml-2">Loading {title}...</span>
                            </div>
                        )}

                        {(!isLoading && listData.length > 0) ? (
                            listData.map((item) => {
                                // compare as strings to avoid type mismatch (number vs string)
                                const valueStrings = (value || []).map((v) => String(v));
                                const isSelected = valueStrings.includes(String(item.id));

                                return (
                                    <label key={item.id ?? String(item.name)} className="flex items-center text-gray-300 text-sm cursor-pointer hover:text-white">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox h-[1rem] w-[1rem] text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 transition duration-150"
                                            checked={!!isSelected}
                                            onChange={() => {
                                                if (typeof onChangeValue !== 'function') return;

                                                let newValue = [...(value || [])];
                                                if (isSelected) {
                                                    newValue = newValue.filter((lid) => String(lid) !== String(item.id));
                                                } else {
                                                    newValue.push(item.id);
                                                }
                                                // Ensure unique values
                                                newValue = Array.from(new Set(newValue));
                                                onChangeValue(newValue);
                                            }}
                                        />
                                        <span className="text-[0.75rem] ml-2 font-light">{item.name || '-'}</span>
                                    </label>
                                );
                            })
                        ) : (
                            (!isHideMsg && !isLoading) &&
                            <div className="flex flex-col items-center py-2">
                                {(!isNestedList || isLoading) && <span className="text-[0.75rem] text-gray-700 ml-2">Data not available</span>}
                            </div>
                        )}

                    </div>
                )}
            </div>
        </div>
    );
};
export { FilterGroup }